/** Help — the readme as a documentation page.
 *
 * The text is the server's chainlit.md (signals-copilot server/chainlit.md),
 * the same file the mobile app shows in its Readme sheet; see
 * components/help/docs.ts for why it carries no links and this page adds them.
 *
 * Built for Chrome on a phone first: the sections are a sticky row of chips
 * under the header, and a tap scrolls to one. On a wide screen the same list
 * sits beside the text. Each section has its own address (/help#dashboard),
 * and the browser's back button leaves the page, which a dialog could not do.
 *
 * Socketless (App.tsx SOCKETLESS_ROUTES): reading the help must not reserve a
 * chat container.
 */
import { ArrowRight, BookOpen } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useConfig } from '@chainlit/react-client';

import { Markdown } from '@/components/Markdown';
import { Header } from '@/components/header';
import { DocSection, linkFor, parseDoc } from '@/components/help/docs';
import { Button } from '@/components/ui/button';
import { SidebarProvider } from '@/components/ui/sidebar';

import { useFeatures } from '@/hooks/useFeatures';

export default function Help() {
  const { config } = useConfig();
  const { features } = useFeatures();
  const navigate = useNavigate();
  const doc = useMemo(() => parseDoc(config?.markdown || ''), [config?.markdown]);
  const [active, setActive] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const chips = useRef<HTMLDivElement>(null);
  // While a tap is scrolling the page, the sections it passes must not take
  // the highlight from the one that was tapped.
  const jumpingUntil = useRef(0);

  const go = useCallback((id: string, smooth = true) => {
    const el = document.getElementById(id);
    const root = scroller.current;
    if (!el || !root) return;
    // Scrolled here rather than with scrollIntoView: that moves every
    // scrolling ancestor, and centring the chip row would then cancel the
    // page's own smooth scroll halfway.
    const sticky = chips.current?.parentElement?.offsetHeight ?? 0;
    const top =
      el.getBoundingClientRect().top -
      root.getBoundingClientRect().top +
      root.scrollTop -
      sticky -
      12;
    jumpingUntil.current = Date.now() + 900;
    root.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
    setActive(id);
    // The address follows, so a section can be shared or reopened.
    window.history.replaceState(null, '', `#${id}`);
  }, []);

  // Arriving at /help#dashboard opens on that section.
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id && doc.sections.some((s) => s.id === id)) {
      requestAnimationFrame(() => go(id, false));
    }
  }, [doc.sections.length]);

  // The chip of the section being read is highlighted as the page scrolls.
  useEffect(() => {
    const root = scroller.current;
    if (!root || !doc.sections.length) return;
    const seen = new Map<string, boolean>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => seen.set(e.target.id, e.isIntersecting));
        if (Date.now() < jumpingUntil.current) return;
        const first = doc.sections.find((s) => seen.get(s.id));
        if (first) setActive(first.id);
      },
      { root, rootMargin: '-64px 0px -55% 0px' }
    );
    doc.sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [doc.sections]);

  // Keep the active chip centred in the row on a phone. Only the row moves.
  useEffect(() => {
    const row = chips.current;
    if (!active || !row) return;
    const chip = row.querySelector<HTMLElement>(`[data-chip="${active}"]`);
    if (!chip) return;
    row.scrollTo({
      left: chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2,
      behavior: 'smooth'
    });
  }, [active]);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full flex-col">
        <Header />
        <div ref={scroller} className="flex-1 overflow-auto">
          {!doc.sections.length && !doc.intro ? (
            <div className="mx-auto max-w-2xl px-4 py-16 text-center text-sm text-muted-foreground">
              No help is published for this workspace yet.
            </div>
          ) : (
            <>
              {/* Phone: the sections as a sticky row of chips. */}
              <nav
                aria-label="Sections"
                className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur lg:hidden"
              >
                <div
                  ref={chips}
                  className="flex gap-2 overflow-x-auto px-4 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {doc.sections.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      data-chip={s.id}
                      onClick={() => go(s.id)}
                      aria-current={active === s.id ? 'true' : undefined}
                      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                        active === s.id
                          ? 'bg-foreground text-background'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {s.title}
                    </button>
                  ))}
                </div>
              </nav>

              <div className="mx-auto grid max-w-5xl gap-8 px-4 pb-24 pt-5 sm:px-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:pt-10">
                {/* Wide screens: the same list beside the text. */}
                <aside className="hidden lg:block">
                  <div className="sticky top-6">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      On this page
                    </div>
                    <ul className="flex flex-col gap-0.5 border-l">
                      {doc.sections.map((s) => (
                        <li key={s.id}>
                          <a
                            href={`#${s.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              go(s.id);
                            }}
                            className={`-ml-px block border-l-2 py-1 pl-3 text-sm transition-colors ${
                              active === s.id
                                ? 'border-primary font-medium text-foreground'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {s.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </aside>

                <main className="min-w-0">
                  <header className="mb-6">
                    <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                      <BookOpen className="size-3.5" /> Help
                    </div>
                    {doc.title && (
                      <h1 className="text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
                        {doc.title}
                      </h1>
                    )}
                    {doc.intro && (
                      <Body className="mt-3 text-[15px] text-muted-foreground">
                        {doc.intro}
                      </Body>
                    )}
                  </header>

                  <div className="flex flex-col gap-4">
                    {doc.sections.map((s) => (
                      <Section
                        key={s.id}
                        section={s}
                        link={linkFor(s.id, features as Record<string, boolean>)}
                        onOpen={(path) => navigate(path)}
                      />
                    ))}
                  </div>
                </main>
              </div>
            </>
          )}
        </div>
      </div>
    </SidebarProvider>
  );
}

function Section({
  section,
  link,
  onOpen
}: {
  section: DocSection;
  link: ReturnType<typeof linkFor>;
  onOpen: (path: string) => void;
}) {
  return (
    <section
      id={section.id}
      className="rounded-2xl border bg-card p-4 sm:p-6"
    >
      <h2 className="text-lg font-semibold tracking-tight">{section.title}</h2>
      <Body className="mt-2">{section.body}</Body>
      {link && (
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => onOpen(link.path)}
          >
            {link.label} <ArrowRight className="size-4" />
          </Button>
        </div>
      )}
    </section>
  );
}

function Body({
  children,
  className = ''
}: {
  children: string;
  className?: string;
}) {
  const { config } = useConfig();
  return (
    <Markdown
      className={`help-body flex flex-col ${className}`}
      allowHtml={config?.features?.unsafe_allow_html}
      latex={config?.features?.latex}
      renderMarkdown={true}
    >
      {children}
    </Markdown>
  );
}
